import { useEffect, lazy, Suspense } from 'react'
import { BrowserRouter as Router, Route, useLocation } from 'react-router-dom'
import { haptic } from '@/lib/haptic'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { Toaster } from '@/components/ui/toaster'
import { AuthProvider } from '@/contexts/AuthContext'
import { BookingProvider } from '@/contexts/BookingContext'
import Home from '@/pages/Home'
import Growth from '@/pages/Growth'
import Strategy from '@/pages/Strategy'
import Design from '@/pages/Design'
import Engineering from '@/pages/Engineering'
import Insights from '@/pages/Insights'
import Automation from '@/pages/Automation'
import Authority from '@/pages/Authority'
import Auth from '@/pages/Auth'
import GetStarted from '@/pages/GetStarted'
import PrivacyPolicy from '@/pages/PrivacyPolicy'
import TermsOfService from '@/pages/TermsOfService'
import About from '@/pages/About'
import CompleteAccount from '@/pages/CompleteAccount'
import ClientPortal from '@/pages/ClientPortal'
import NotFound from '@/pages/NotFound'
import BarbaRoutes from '@/components/barba/BarbaRoutes'
import PortalDashboard from '@/pages/portal/PortalDashboard'
import PortalSettings from '@/pages/portal/PortalSettings'
import PortalBusinesses from '@/pages/portal/PortalBusinesses'
import PortalAddCompany from '@/pages/portal/PortalAddCompany'
import PortalCompanyDetail from '@/pages/portal/PortalCompanyDetail'
import PortalUsers from '@/pages/portal/PortalUsers'
import PortalBriefs from '@/pages/portal/PortalBriefs'
import './index.css'

/** Ad LP — available in local/dev only until placeholder content is replaced. */
const GrowthLanding = import.meta.env.DEV
  ? lazy(() => import('@/pages/GrowthLanding'))
  : null

/** Client concept LP — The Pet Practice cat services. */
const TppCatServicesLanding = lazy(() => import('@/pages/TppCatServicesLanding'))
/** Client concept LP — The Pet Practice local vet. */
const TppLocalVetLanding = lazy(() => import('@/pages/TppLocalVetLanding'))
const TradiesLanding = lazy(() => import('@/pages/TradiesLanding'))

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: 1,
    },
  },
})

function HapticClickHandler() {
  useEffect(() => {
    // Checkboxes: haptic only when state actually changes (not on focus/touch)
    const handleChange = (e: Event) => {
      if (!e.isTrusted) return
      const target = e.target as HTMLInputElement
      if (target?.type === 'checkbox') {
        haptic()
      }
    }

    // Buttons/links: haptic only when click completes (not on touch-down)
    const handleClick = (e: MouseEvent) => {
      if (!e.isTrusted) return
      const target = (e.target as HTMLElement)
      if (
        target.tagName === 'BUTTON' ||
        target.tagName === 'A' ||
        target.getAttribute('role') === 'button' ||
        target.closest('button, a[href]')
      ) {
        haptic()
      }
    }

    document.addEventListener('change', handleChange, true)
    document.addEventListener('click', handleClick, true)
    return () => {
      document.removeEventListener('change', handleChange, true)
      document.removeEventListener('click', handleClick, true)
    }
  }, [])
  return null
}

function ScrollToTop() {
  const { pathname, hash } = useLocation()
  useEffect(() => {
    if (hash) {
      const id = hash.slice(1)
      const scrollToElement = () => {
        const el = document.getElementById(id)
        if (el) el.scrollIntoView({ behavior: 'smooth' })
      }
      setTimeout(scrollToElement, 100)
    }
    // Page scroll for route changes is handled after Barba transitions complete.
  }, [pathname, hash])
  return null
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
      <Router>
        <BookingProvider>
        <HapticClickHandler />
        <ScrollToTop />
        <div className="min-h-screen bg-background font-sans antialiased">
          <BarbaRoutes>
            <Route path="/" element={<Home />} />
            <Route path="/growth" element={<Growth />} />
            {import.meta.env.DEV && GrowthLanding && (
              <Route
                path="/lp/growth"
                element={
                  <Suspense fallback={null}>
                    <GrowthLanding />
                  </Suspense>
                }
              />
            )}
            <Route
              path="/lp/tpp-cat-services"
              element={
                <Suspense fallback={null}>
                  <TppCatServicesLanding />
                </Suspense>
              }
            />
            <Route
              path="/lp/tpp-local-vet"
              element={
                <Suspense fallback={null}>
                  <TppLocalVetLanding />
                </Suspense>
              }
            />
            <Route
              path="/lp/tradies"
              element={
                <Suspense fallback={null}>
                  <TradiesLanding />
                </Suspense>
              }
            />
            <Route path="/strategy" element={<Strategy />} />
            <Route path="/design" element={<Design />} />
            <Route path="/engineering" element={<Engineering />} />
            <Route path="/insights" element={<Insights />} />
            <Route path="/automation" element={<Automation />} />
            <Route path="/authority" element={<Authority />} />
            <Route path="/login" element={<Auth />} />
            <Route path="/portal" element={<ClientPortal />}>
              <Route index element={<PortalDashboard />} />
              <Route path="briefs" element={<PortalBriefs />} />
              <Route path="businesses" element={<PortalBusinesses />} />
              <Route path="businesses/new" element={<PortalAddCompany />} />
              <Route path="businesses/:id" element={<PortalCompanyDetail />} />
              <Route path="users" element={<PortalUsers />} />
              <Route path="settings" element={<PortalSettings />} />
            </Route>
            <Route path="/new-project" element={<GetStarted />} />
            <Route path="/complete-account" element={<CompleteAccount />} />
            <Route path="/about" element={<About />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<TermsOfService />} />
            <Route path="*" element={<NotFound />} />
          </BarbaRoutes>
          <Toaster />
        </div>
        </BookingProvider>
      </Router>
      </AuthProvider>
    </QueryClientProvider>
  )
}

export default App
